<template>
  <div class="bg-white rounded-2xl shadow-lg p-6">
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-2">
        <span class="text-3xl">❓</span>
        <span>常见问题 FAQ</span>
      </h2>
      <p class="text-gray-600 text-lg">关于霍兰德测试的常见疑问解答</p>
    </div>

    <div class="space-y-4">
      <!-- FAQ 1 -->
      <details class="group bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
        <summary class="cursor-pointer p-5 font-semibold text-gray-900 flex items-start gap-3 hover:bg-blue-100 transition-colors">
          <span class="text-xl flex-shrink-0">🤔</span>
          <span class="flex-1">Holland Code 中三个因子的影响有什么区别？</span>
          <span class="text-blue-500 group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div class="px-5 pb-5 space-y-4">
          <p class="text-gray-700 leading-relaxed">
            你的测试结果是一个<strong :style="{ color: primaryColor }">三位代码</strong>（如RAS），这个代码由三个因子组成，每个因子对你的影响程度是不同的。
          </p>
          
          <div class="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
            <p class="font-semibold text-yellow-900 mb-2">📊 理解因子的权重</p>
            <p class="text-yellow-800 text-sm leading-relaxed">
              <strong>因子的影响是逐级递减的：</strong><br>
              • 第一个因子（如RAS中的<strong>R</strong>）对你的影响<strong>最大</strong><br>
              • 第二个因子（如RAS中的<strong>A</strong>）影响次之<br>
              • 第三个因子（如RAS中的<strong>S</strong>）影响相对较小
            </p>
            <div class="mt-3 p-3 bg-white/50 rounded">
              <p class="text-yellow-900 text-xs">
                💡 比如RAS和RAI虽然只差一个字母，但随着环境、经历的变化，<strong>RAS可能会变成RAI</strong>，这是正常的动态变化过程。
              </p>
            </div>
          </div>

          <div class="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p class="font-semibold text-blue-900 mb-2">💡 实用建议</p>
            <p class="text-blue-800 text-sm leading-relaxed">
              🤝 建议：<strong>找到同样代码（如RAS）的朋友交流</strong>，了解他们目前在什么行业、从事什么工作，这能给你提供更具体的职业参考和发展路径。他们的实际经验比理论建议更有参考价值！
            </p>
          </div>
        </div>
      </details>

      <!-- FAQ 2 -->
      <details class="group bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 overflow-hidden">
        <summary class="cursor-pointer p-5 font-semibold text-gray-900 flex items-start gap-3 hover:bg-purple-100 transition-colors">
          <span class="text-xl flex-shrink-0">📊</span>
          <span class="flex-1">分数的高低重要吗？低分是不是代表不适合？</span>
          <span class="text-purple-500 group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div class="px-5 pb-5 space-y-4">
          <p class="text-gray-700 leading-relaxed">
            <strong class="text-red-600">不！</strong>霍兰德测试关注的是<strong :style="{ color: primaryColor }">相对排名</strong>，而非绝对分数。
          </p>
          
          <div class="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
            <p class="font-semibold text-green-900 mb-2">✅ 关键理解</p>
            <p class="text-green-800 text-sm leading-relaxed">
              • 测试结果显示的是你在六个维度上的<strong>相对强弱</strong><br>
              • 即使某个维度分数较低，只要它在你的前三位，就说明这是你相对更感兴趣的方向<br>
              • 低分只是说明相对于你的其他兴趣，这个方向不是最突出的
            </p>
          </div>

          <div class="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
            <p class="font-semibold text-amber-900 mb-2">🎯 举例说明</p>
            <p class="text-amber-800 text-sm leading-relaxed">
              如果你的R（实际型）得分是60分，I（研究型）是55分，A（艺术型）是50分，那么你的Holland Code就是RIA。虽然绝对分数不高，但这三个维度是你最感兴趣的领域。
            </p>
          </div>
        </div>
      </details>

      <!-- FAQ 3 -->
      <details class="group bg-gradient-to-r from-green-50 to-teal-50 rounded-xl border border-green-200 overflow-hidden">
        <summary class="cursor-pointer p-5 font-semibold text-gray-900 flex items-start gap-3 hover:bg-green-100 transition-colors">
          <span class="text-xl flex-shrink-0">🔄</span>
          <span class="flex-1">测试结果会随时间改变吗？</span>
          <span class="text-green-500 group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div class="px-5 pb-5 space-y-4">
          <p class="text-gray-700 leading-relaxed">
            是的！你的职业兴趣可能会随着<strong>年龄、经历、环境</strong>的变化而发生变化。
          </p>
          
          <div class="bg-blue-50 p-4 rounded-lg">
            <p class="text-blue-800 text-sm leading-relaxed">
              • 建议每隔1-2年重新测试一次<br>
              • 特别是在经历重大生活变化后（如升学、工作转换等）<br>
              • 关注变化趋势，而不仅仅是单次结果
            </p>
          </div>
        </div>
      </details>

      <!-- FAQ 4 -->
      <details class="group bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200 overflow-hidden">
        <summary class="cursor-pointer p-5 font-semibold text-gray-900 flex items-start gap-3 hover:bg-orange-100 transition-colors">
          <span class="text-xl flex-shrink-0">🎓</span>
          <span class="flex-1">如果我的专业和测试结果不匹配怎么办？</span>
          <span class="text-orange-500 group-open:rotate-180 transition-transform">▼</span>
        </summary>
        <div class="px-5 pb-5 space-y-4">
          <p class="text-gray-700 leading-relaxed">
            不用担心！测试结果是<strong>参考工具</strong>，不是限制标签。
          </p>
          
          <div class="bg-purple-50 p-4 rounded-lg">
            <p class="text-purple-800 text-sm leading-relaxed">
              • 可以在当前专业中寻找符合你兴趣的方向<br>
              • 很多职业是跨专业的，关键是找到兴趣点<br>
              • 考虑在工作中发挥你的兴趣特长<br>
              • 培养副业或爱好来满足其他兴趣维度
            </p>
          </div>
        </div>
      </details>
    </div>
  </div>
</template>

<script>
/**
 * 常见问题FAQ组件
 * 使用原生details/summary实现折叠面板
 */
export default {
  name: 'HollandFAQ',
  props: {
    primaryColor: {
      type: String,
      default: '#6366f1'
    }
  }
}
</script>

<style scoped>
details summary::-webkit-details-marker {
  display: none;
}

details summary {
  list-style: none;
}
</style>

